import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Loader2, AlertCircle, RefreshCw, FileText, TrendingUp, Users, Bell,
    Filter, Clock, X, Send, Globe,
} from 'lucide-react';
import { fetchPropertyCampaignAnalytics } from '../../../features/analytics/api/analyticsApi';
import { syncVirtualContact } from '../../../api/contact';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';
import BuyerActionsModal from '../BuyerActionsModal';
import CampaignHealthCard from './CampaignHealthCard';
import ReaPerformancePanel from './ReaPerformancePanel';
import CampaignSummaryGrid from './CampaignSummaryGrid';
import BuyerFunnel from './BuyerFunnel';
import TopBuyersTable from './TopBuyersTable';
import EngagementCharts from './EngagementCharts';
import AgentAlerts from './AgentAlerts';
import { RepeatBuyers, SourceAttribution, OpenHomeAnalytics, BenchmarkPanel } from './CampaignPanels';
import VendorReportModal from './VendorReportModal';
import PropertyCommunications from './PropertyCommunications';

const PERIODS = [
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: 'all', label: 'Full campaign' },
];

function Section({ title, subtitle, icon: Icon, children, action }) {
    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="flex items-center gap-2 text-base font-bold text-[#374151]">
                        {Icon && <Icon size={16} className="text-[#55C5D0]" />}
                        {title}
                    </h4>
                    {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

/** Buyers behind one funnel stage. */
function StageBuyersModal({ stage, buyersByKey, onClose, onBuyerClick }) {
    if (!stage) return null;
    const buyers = (stage.buyer_keys || []).map((key) => buyersByKey[key]).filter(Boolean);
    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                    <h3 className="text-base font-bold text-[#374151]">
                        {stage.label} ({stage.count})
                        {stage.count > buyers.length && (
                            <span className="ml-2 text-xs font-medium text-gray-500">
                                showing the top {buyers.length}
                            </span>
                        )}
                    </h3>
                    <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>
                <div className="divide-y divide-gray-100">
                    {buyers.map((buyer) => (
                        <button
                            key={buyer.key}
                            type="button"
                            onClick={() => onBuyerClick(buyer)}
                            className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-[#f8fafb]"
                        >
                            <span className="min-w-0">
                                <span className="block truncate font-semibold text-[#374151]">{buyer.name}</span>
                                {buyer.email && <span className="block truncate text-xs text-gray-500">{buyer.email}</span>}
                            </span>
                            <span className="ml-3 shrink-0 text-sm font-bold text-[#3a99a3]">
                                {buyer.intent_score}
                            </span>
                        </button>
                    ))}
                    {!buyers.length && (
                        <p className="px-5 py-6 text-center text-sm text-gray-500">No buyers in this stage.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * The people behind one headline tile, e.g. who saved the listing (CG, Monday
 * 13403665362). Rows arrive with the metric, newest first.
 */
function MetricPeopleModal({ metric, periodLabel, onClose, onBuyerClick }) {
    if (!metric) return null;
    const people = metric.people || [];
    const total = metric.people_total ?? people.length;
    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                    <div>
                        <h3 className="text-base font-bold text-[#374151]">
                            {metric.label} ({new Intl.NumberFormat('en-AU').format(metric.value || 0)})
                        </h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {total} {total === 1 ? 'person' : 'people'}, {periodLabel.toLowerCase()}
                            {total > people.length && `, showing the latest ${people.length}`}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="rounded-lg p-1 hover:bg-gray-100">
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>
                <div className="divide-y divide-gray-100">
                    {people.map((person) => (
                        <button
                            key={person.key}
                            type="button"
                            onClick={() => onBuyerClick(person)}
                            className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-[#f8fafb]"
                        >
                            <span className="min-w-0">
                                <span className="block truncate font-semibold text-[#374151]">{person.name}</span>
                                {(person.email || person.phone) && (
                                    <span className="block truncate text-xs text-gray-500">
                                        {[person.email, person.phone].filter(Boolean).join(' · ')}
                                    </span>
                                )}
                            </span>
                            <span className="ml-3 shrink-0 text-right">
                                <span className="block text-xs font-semibold text-[#374151]">
                                    {formatMelbourneShortDate(person.last_at)}
                                </span>
                                {person.count > 1 && (
                                    <span className="block text-[11px] text-gray-500">{person.count} times</span>
                                )}
                            </span>
                        </button>
                    ))}
                    {!people.length && (
                        <p className="px-5 py-6 text-center text-sm text-gray-500">
                            No named buyers behind this number yet.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * The campaign intelligence centre for one property.
 *
 * Answers the four questions the agent actually has: what is happening with this
 * campaign, who are the serious buyers, what should I do next, and what do I tell
 * the vendor.
 */
export default function PropertyCampaignDashboard({ propertyId, reaListingId = '', ownerEmail = '', ownerName = '' }) {
    const [period, setPeriod] = useState('30');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [actionsBuyer, setActionsBuyer] = useState(null);
    const [stage, setStage] = useState(null);
    const [metric, setMetric] = useState(null);
    const [reportOpen, setReportOpen] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async (selectedPeriod, refresh = false) => {
        if (!propertyId) return;
        if (refresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const payload = await fetchPropertyCampaignAnalytics(propertyId, selectedPeriod, refresh);
            setData(payload);
        } catch (err) {
            setError('Could not load the campaign analytics for this property.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [propertyId]);

    useEffect(() => {
        load(period);
    }, [load, period]);

    const buyersByKey = useMemo(() => {
        const map = {};
        (data?.buyers || []).forEach((buyer) => { map[buyer.key] = buyer; });
        return map;
    }, [data]);

    const handleBuyerClick = (buyer) => {
        if (!buyer) return;
        // The quick-actions popup resolves the buyer to a CRM contact, so it needs
        // an id or an email to work with.
        if (!buyer.buyer_id && !buyer.email) {
            toast('This visitor has no contact details recorded yet.');
            return;
        }
        setActionsBuyer({
            id: buyer.buyer_id,
            name: buyer.name,
            email: buyer.email,
            phone: buyer.phone,
        });
    };

    const openBuyerProfile = async (buyer) => {
        if (!buyer?.id) {
            toast.error('No portal account for this buyer.');
            return;
        }
        const toastId = toast.loading(`Opening ${buyer.name || buyer.email}'s profile`);
        try {
            const res = await syncVirtualContact(`buyer_${buyer.id}`);
            const contactId = res?.data?.id;
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`);
        } catch {
            toast.error('Could not open buyer profile.', { id: toastId });
        }
    };

    if (loading && !data) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
                <Loader2 size={28} className="animate-spin text-[#55C5D0]" />
                <p className="text-sm">Building the campaign picture</p>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-red-500">
                <AlertCircle size={28} />
                <p className="text-sm">{error}</p>
                <button
                    type="button"
                    onClick={() => load(period, true)}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-[#374151]"
                >
                    Try again
                </button>
            </div>
        );
    }

    const summary = data?.summary || {};
    const hotCount = (data?.buyers || []).filter((b) => b.intent_band === 'hot').length;

    return (
        <div className="space-y-4">
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                    <Filter size={14} className="text-gray-400" />
                    {PERIODS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => setPeriod(option.value)}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                period === option.value
                                    ? 'bg-[#374151] text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2">
                    <span className="hidden items-center gap-1 text-[11px] text-gray-400 sm:flex">
                        <Clock size={11} />
                        Day {summary.days_on_market} of the campaign
                        {summary.days_since_last_enquiry !== null && summary.days_since_last_enquiry !== undefined
                            && `, last enquiry ${summary.days_since_last_enquiry}d ago`}
                    </span>
                    <button
                        type="button"
                        onClick={() => load(period, true)}
                        disabled={refreshing}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-[#374151] hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={() => setReportOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#55C5D0] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#47b1bb]"
                    >
                        <FileText size={13} /> Generate vendor report
                    </button>
                </div>
            </div>

            <CampaignHealthCard health={data?.health} />

            {data?.narrative?.summary && (
                <p className="rounded-2xl border border-gray-200 bg-[#f8fafb] p-4 text-sm text-[#374151]">
                    {data.narrative.summary}
                </p>
            )}

            <Section
                title="Campaign summary"
                subtitle="Click a number to see who is behind it. Movement is measured against the previous 7 days, the previous 30 days and the campaign average."
                icon={TrendingUp}
            >
                <CampaignSummaryGrid metrics={summary.metrics || []} onMetricClick={setMetric} />
            </Section>

            {/* Live REA campaign stats, headlined by Property Price Statement
                views. rea_listing_id is often stored with a "rea_id " prefix
                (same normalisation as the listing links use); only a clean
                numeric id has a Campaign API report - placeholder REA_<pk>
                ids are skipped. */}
            {(() => {
                const reaId = String(reaListingId || '')
                    .replace(/^\s*(rea_id|domain_id|listing_id)\s*/i, '').trim();
                if (!/^\d+$/.test(reaId)) return null;
                return (
                    <Section
                        title="realestate.com.au performance"
                        subtitle="Buyer engagement on the REA listing, including Property Price Statement (PPS) views."
                        icon={Globe}
                    >
                        <ReaPerformancePanel reaListingId={reaId} />
                    </Section>
                );
            })()}

            {/* What we have actually SENT these people, and who opened it. Sits
                directly under the summary because it is the first thing an
                agent asks after a blast (Paul Jimoulis, CRM-2026-00072). */}
            <Section
                title="Communications"
                subtitle="Bulk emails and texts sent from this listing's Enquiries, Attendees and Buyers tabs."
                icon={Send}
            >
                <PropertyCommunications propertyId={propertyId} />
            </Section>

            <Section
                title="Needs your attention"
                subtitle={hotCount ? `${hotCount} buyer${hotCount === 1 ? '' : 's'} scoring hot right now` : 'Live signals from buyer behaviour'}
                icon={Bell}
            >
                <AgentAlerts
                    alerts={data?.alerts || []}
                    buyersByKey={buyersByKey}
                    onBuyerClick={handleBuyerClick}
                />
            </Section>

            <Section
                title="Top buyers to contact today"
                subtitle={
                    data?.buyers_total > (data?.buyers_shown || 0)
                        ? `Ranked by Intent Score out of 100. Showing the top ${data.buyers_shown} of ${data.buyers_total} buyers who touched this listing.`
                        : 'Ranked by Intent Score out of 100. Tap a buyer to log a note, set a reminder or email them.'
                }
                icon={Users}
            >
                <TopBuyersTable buyers={data?.buyers || []} onBuyerClick={handleBuyerClick} />
            </Section>

            <div className="grid gap-4 xl:grid-cols-2">
                <Section title="Buyer engagement funnel" subtitle="Click a stage to see the buyers inside it.">
                    <BuyerFunnel funnel={data?.funnel || []} onStageClick={setStage} />
                </Section>
                <Section title="Engagement over time" subtitle="Is buyer interest rising, flat or falling.">
                    <EngagementCharts data={data?.timeseries || []} />
                </Section>
            </div>

            <Section title="Repeat buyer behaviour" subtitle="The strongest intent signals we hold.">
                <RepeatBuyers
                    repeat={data?.repeat || {}}
                    buyersByKey={buyersByKey}
                    onBuyerClick={handleBuyerClick}
                />
            </Section>

            <div className="grid gap-4 xl:grid-cols-2">
                <Section title="Where the buyers came from" subtitle="Views through to offers, by channel.">
                    <SourceAttribution sources={data?.sources || []} />
                </Section>
                <Section title="Open homes" subtitle="Registrations, attendance and what happened next.">
                    <OpenHomeAnalytics openHomes={data?.open_homes || []} />
                </Section>
            </div>

            <Section title="How this campaign compares" subtitle="Benchmarked against similar Collings campaigns.">
                <BenchmarkPanel benchmark={data?.benchmark} />
            </Section>

            {stage && (
                <StageBuyersModal
                    stage={stage}
                    buyersByKey={buyersByKey}
                    onClose={() => setStage(null)}
                    onBuyerClick={(buyer) => { setStage(null); handleBuyerClick(buyer); }}
                />
            )}

            {metric && (
                <MetricPeopleModal
                    metric={metric}
                    periodLabel={period === 'all' ? 'Full campaign' : `Last ${period} days`}
                    onClose={() => setMetric(null)}
                    onBuyerClick={(person) => { setMetric(null); handleBuyerClick(person); }}
                />
            )}

            {actionsBuyer && (
                <BuyerActionsModal
                    buyer={actionsBuyer}
                    propertyId={propertyId}
                    onClose={() => setActionsBuyer(null)}
                    onViewProfile={openBuyerProfile}
                />
            )}

            {reportOpen && (
                <VendorReportModal
                    propertyId={propertyId}
                    defaultPeriod={period}
                    ownerEmail={ownerEmail}
                    ownerName={ownerName}
                    narrative={data?.narrative?.summary}
                    onClose={() => setReportOpen(false)}
                />
            )}
        </div>
    );
}
